import { cn } from "cn";
import { BwCheckmarkLine } from "@/components/ui/icons";
import { buildPresaleStages } from "../_libs/presale-content";
import type { PresaleStageStatus } from "../_types";
import { PresaleSection } from "./presale-section";

const LABEL_CLASSES: Record<PresaleStageStatus, string> = {
  done: "text-text-primary",
  current: "text-text-primary",
  upcoming: "text-text-dark-low",
};

function StageMarker({ status }: { status: PresaleStageStatus }) {
  if (status === "done") {
    return (
      <span className="mt-0.75 inline-flex size-5.5 items-center justify-center rounded-full bg-bg-primary-max text-text-light">
        <BwCheckmarkLine size={12} />
      </span>
    );
  }
  if (status === "current") {
    return (
      <span className="mt-0.75 size-5.5 rounded-full border-[3px] border-border-primary-max bg-bg-light" />
    );
  }
  return (
    <span className="mt-0.75 size-5.5 rounded-full border-2 border-border-dark-high bg-bg-light" />
  );
}

export function PresaleNextSteps({ placeLabel }: { placeLabel: string }) {
  const stages = buildPresaleStages(placeLabel);

  return (
    <PresaleSection title="What happens next?">
      <ol className="mt-6 flex flex-col">
        {stages.map((stage, index) => {
          const isLast = index === stages.length - 1;
          return (
            <li
              key={stage.label}
              className={cn(
                /* sorted */
                "relative grid",
                "grid-cols-[22px_1fr] gap-x-[18px]",
                !isLast && "pb-8",
              )}
            >
              {!isLast && (
                <span
                  aria-hidden
                  className={cn(
                    /* sorted */
                    "absolute top-[26px] bottom-0 left-[10px]",
                    "w-0.5",
                    stage.status === "done"
                      ? "bg-bg-primary-max"
                      : "bg-bg-dark-min",
                  )}
                />
              )}
              <StageMarker status={stage.status} />
              <div
                className={cn(
                  /* sorted */
                  "flex",
                  "flex-col",
                  stage.items.length > 1 ? "gap-2.5" : "gap-1",
                )}
              >
                <div
                  className={cn(
                    /* sorted */
                    "text-xs leading-4 font-semibold tracking-wider uppercase",
                    LABEL_CLASSES[stage.status],
                  )}
                >
                  {stage.label}
                </div>
                {stage.items.map((item) => (
                  <div key={item.title} className="flex flex-col gap-0.5">
                    <div className="text-base leading-normal font-medium text-text-dark-max">
                      {item.title}
                    </div>
                    <div className="text-sm leading-5 text-text-dark-high text-pretty">
                      {item.body}
                    </div>
                  </div>
                ))}
              </div>
            </li>
          );
        })}
      </ol>
    </PresaleSection>
  );
}
